<template>
    <div class="pt-32 min-h-screen bg-background font-sans flex items-center justify-center p-4">
        <div
            class="w-full max-w-4xl bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col md:flex-row animate-fade-up">
            <!-- Left side promotional banner -->
            <div class="w-full md:w-1/2 bg-primary/5 p-12 flex flex-col justify-center relative overflow-hidden group">
                <div
                    class="absolute inset-0 bg-gradient-to-br from-primary/10 to-secondary/10 z-0 transition-transform duration-700 group-hover:scale-105">
                </div>
                <div class="relative z-10">
                    <h2 class="text-4xl md:text-5xl font-serif text-primary mb-6">Share Your Story</h2>
                    <p class="text-muted-foreground text-lg leading-relaxed">
                        We love hearing from our community! Leave a testimonial below and let others know about your
                        experience.
                    </p>
                </div>
            </div>

            <!-- Right side form -->
            <div class="w-full md:w-1/2 p-8 md:p-12">
                <h3 class="text-2xl font-serif mb-8 text-gray-800">Testimonial</h3>
                <form @submit.prevent="handleSubmit" class="space-y-5" novalidate>
                    <div class="space-y-2">
                        <label for="name" class="text-sm font-medium text-gray-700">Name</label>
                        <input id="name" v-model="form.name" type="text" required
                            class="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                            placeholder="Your Name" />
                    </div>

                    <div class="space-y-2">
                        <label for="email" class="text-sm font-medium text-gray-700">Email</label>
                        <input id="email" v-model="form.email" type="email" required
                            class="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                            placeholder="your@email.com" />
                    </div>

                    <div class="space-y-2">
                        <label for="message" class="text-sm font-medium text-gray-700">Your Message</label>
                        <textarea id="message" v-model="form.message" rows="4" required
                            class="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                            placeholder="Tell us about your experience..."></textarea>
                    </div>

                    <div class="space-y-2">
                        <label class="text-sm font-medium text-gray-700">Rating</label>
                        <div class="flex items-center space-x-1">
                            <span v-for="star in 5" :key="star" @click="form.rating = star"
                                :class="['text-2xl cursor-pointer transition-colors', form.rating >= star ? 'text-yellow-400' : 'text-gray-300']">
                                ★
                            </span>
                        </div>
                    </div>

                    <!-- reCAPTCHA token hidden field -->
                    <input type="hidden" v-model="form.recaptchaToken" />

                    <button type="submit"
                        class="w-full bg-primary text-white font-medium py-3  hover:bg-primary/90 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
                        :disabled="isSubmitting">
                        {{ isSubmitting ? 'Submitting...' : 'Submit Testimonial' }}
                    </button>
                </form>
            </div>
        </div>

        <!-- Toast Notification -->
        <transition name="fade">
            <div v-if="toast.visible"
                class="fixed top-5 right-5 bg-green-600 text-white px-6 py-4 rounded-lg shadow-xl z-50 flex items-center gap-3">
                <span class="font-bold">Success</span>
                <span>{{ toast.message }}</span>
            </div>
        </transition>

        <!-- load recaptcha script only once -->
        <div id="recaptcha-container"></div>
    </div>
</template>

<script>
import api from '@/services/api'
import ConstantPage from '@/components/utilities/ConstantPage.vue'

export default {
    name: 'TestimonialPage',
    mixins: [ConstantPage],
    data() {
        return {
            form: {
                name: '',
                email: '',
                message: '',
                rating: 5,
                recaptchaToken: ''
            },
            isSubmitting: false,
            toast: {
                visible: false,
                message: ''
            },
            recaptchaSiteKey: process.env.VUE_APP_RECAPTCHA_SITE_KEY || 'YOUR_SITE_KEY'
            // recaptchaSiteKey: '6LexO20sAAAAAPZFJjrSptfFmciAYJ56tV8IT3e7' // test key that always returns success
        }
    },
    mounted() {
        this.loadRecaptcha();
    },
    methods: {
        loadRecaptcha() {
            if (window.grecaptcha) return // already loaded

            const script = document.createElement('script')
            script.src = `https://www.google.com/recaptcha/api.js?render=${this.recaptchaSiteKey}`
            script.async = true
            script.defer = true
            document.head.appendChild(script)
        },
        async handleSubmit() {
            if (!window.grecaptcha) {
                alert('Unable to load reCAPTCHA. Please try again later.')
                return
            }

            this.isSubmitting = true

            try {
                // execute v3 recaptcha using ready callback (ready() does not return a promise)
                const token = await new Promise((resolve, reject) => {
                    window.grecaptcha.ready(async () => {
                        try {
                            const t = await window.grecaptcha.execute(this.recaptchaSiteKey, { action: 'submit' })
                            resolve(t)
                        } catch (e) {
                            reject(e)
                        }
                    })
                })

                this.form.recaptchaToken = token
                this.form.companyid = this.COMPANY_ID // add companyid to form data;


                const response = await api.post('api/testimonial/store', this.form)

                if (response.data && response.data.success) {
                    this.showToast(response.data.message || 'Thank you for your testimonial!')
                    this.resetForm()
                } else {
                    throw new Error('Server rejected testimonial')
                }
            } catch (error) {
                console.error(error)
                alert('Failed to submit testimonial. Please try again later.')
            } finally {
                this.isSubmitting = false
            }
        },
        resetForm() {
            this.form = {
                name: '',
                email: '',
                message: '',
                rating: 5,
                recaptchaToken: ''
            }
        },
        showToast(message) {
            this.toast.message = message
            this.toast.visible = true
            setTimeout(() => {
                this.toast.visible = false
            }, 3000)
        }
    }
}
</script>

<style scoped>
.animate-fade-up {
    animation: fadeUp 0.8s ease-out forwards;
}

@keyframes fadeUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}
</style>
